import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Sparkles, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { useHousehold } from "@/context/HouseholdContext";
import { api } from "@/lib/api";
import { setActiveCompany } from "@/lib/activeCompany";

export const TOUR_KEY = "cashmy.tour";
export const startTour = (space) => { sessionStorage.setItem(TOUR_KEY, space); window.dispatchEvent(new Event("cashmy-tour")); };

const STEPS = {
  personal: (t) => [
    { to: "/", sel: '[data-testid="budget-available-value"]', title: "Votre reste à vivre, en temps réel", text: "CASHMY additionne vos comptes, retire les charges à venir et vous dit ce qu'il vous reste vraiment jusqu'à la fin du mois." },
    { to: `/foyer/equipements/${t.asset_id}`, sel: ['[data-testid="documents-list"]', '[data-testid="asset-documents-box"]'], title: "Où se trouve la facture de mon réfrigérateur ?", text: "Factures, notices et garanties sont rangées avec chaque appareil. Fini les tiroirs : un clic pour la retrouver ou la télécharger." },
    { to: `/foyer/equipements/${t.asset_id}`, sel: '[data-testid="asset-warranty-status"]', title: "Jusqu'à quand est-il garanti ?", text: "La date de fin de garantie est suivie pour chaque équipement, avec une alerte avant qu'elle n'expire." },
    { to: `/foyer/equipements/${t.asset_id}`, sel: '[data-testid="asset-insurance"]', title: "Quel contrat couvre cet appareil ?", text: "CASHMY relie vos contrats d'assurance à vos biens : vous savez tout de suite qui appeler en cas de panne ou de sinistre." },
    { to: "/foyer?tab=assurances", sel: '[data-testid="review-saving-year"]', title: "Combien puis-je économiser ?", text: "Vos contrats sont comparés au marché : CASHMY chiffre l'économie possible par an et repère les doublons de garanties." },
    { to: "/finances", sel: null, title: "À vous de jouer", text: "Cette démo est la vôtre : ajoutez une dépense, un équipement ou un objectif, rien n'est partagé. Essayez aussi la démo Entreprise." },
  ],
  business: () => [
    { to: "/entreprise", sel: '[data-testid="biz-intercompany"]', title: "Ce que vos entreprises se doivent", text: "Les dépenses payées pour une autre de vos entreprises sont suivies ici, avec ce qu'il reste à refacturer ou à rembourser." },
    { to: "/entreprise/depenses", sel: '[data-testid^="mismatch-"]', title: "Une facture au nom d'une autre entreprise", text: "CASHMY détecte les incohérences : cette facture est adressée au Studio. En un clic, refacturez-la, transférez-la ou gardez-la." },
    { to: "/entreprise/factures", sel: '[data-testid="open-actions"]', title: "Notes et actions à faire", text: "Relances, refacturations, vérifications : chaque facture ou dette peut porter une note ou une action avec une date limite." },
    { to: "/entreprise/equipements", sel: '[data-testid="eq-card-warranty"]', title: "Garanties et réparateurs du matériel", text: "Chaque équipement a sa garantie, son prochain entretien et les coordonnées de son réparateur." },
    { to: "/entreprise/charges", sel: '[data-testid="plan-peak"]', title: "Les charges des 12 prochains mois", text: "Hausse de loyer, fin de leasing, nouvel abonnement : programmez les changements et voyez leur effet mois par mois." },
    { to: "/entreprise/fournisseurs", sel: '[data-testid="mailbox-import"]', title: "Vos factures arrivent toutes seules", text: "Connectez vos boîtes email : chaque facture devient une dépense classée, et CASHMY vérifie avec quelle carte elle a été payée." },
  ],
};

const useTarget = (sel, key) => {
  const [rect, setRect] = useState(null);
  useEffect(() => {
    setRect(null);
    if (!sel) return undefined;
    let el = null; let tries = 0; let settled = 0;
    const zone = () => window.innerHeight - (window.innerWidth < 768 ? 400 : 260);
    const id = setInterval(() => {
      if (!el) {
        el = [].concat(sel).map((s) => document.querySelector(s)).find(Boolean) || null;
        if (!el && ++tries > 40) clearInterval(id);
      }
      if (!el) return;
      const r = el.getBoundingClientRect();
      if ((r.top < 70 || r.top > zone()) && settled < 25) {
        settled += 1;
        window.scrollTo({ top: r.top + window.scrollY - 90, behavior: settled === 1 ? "smooth" : "auto" });
      }
      setRect(el.getBoundingClientRect());
    }, 150);
    return () => clearInterval(id);
  }, [String(sel), key]); // eslint-disable-line react-hooks/exhaustive-deps
  return rect;
};

export const DemoTour = () => {
  const { user } = useAuth();
  const { select } = useHousehold();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const [state, setState] = useState(null);
  const begin = useCallback(async () => {
    const space = sessionStorage.getItem(TOUR_KEY);
    if (!space || !user?.sandbox) return;
    sessionStorage.removeItem(TOUR_KEY);
    const { data } = await api.get("/demo/tour");
    if (space === "personal" && data.household_id) select(data.household_id);
    if (data.company_id) setActiveCompany(data.company_id);
    setState({ steps: STEPS[space](data), i: 0 });
  }, [user, select]);
  useEffect(() => {
    begin();
    window.addEventListener("cashmy-tour", begin);
    return () => window.removeEventListener("cashmy-tour", begin);
  }, [begin]);
  const step = state?.steps[state.i];
  useEffect(() => {
    if (step && `${pathname}${search}` !== step.to) navigate(step.to);
  }, [step]); // eslint-disable-line react-hooks/exhaustive-deps
  const rect = useTarget(step?.sel, state?.i);
  if (!step) return null;
  const last = state.i === state.steps.length - 1;
  const move = (d) => setState({ ...state, i: state.i + d });
  const pad = 8;
  const atTop = rect && rect.top + rect.height / 2 > window.innerHeight * 0.5;
  return (
    <div className="fixed inset-0 z-[80]" data-testid="demo-tour">
      {rect ? <div className="pointer-events-none absolute rounded-2xl ring-4 ring-[#8ce2b8] transition-all duration-300"
        style={{ top: rect.top - pad, left: rect.left - pad, width: rect.width + pad * 2, height: rect.height + pad * 2, boxShadow: "0 0 0 9999px rgba(10,25,32,.62)" }} />
        : <div className="absolute inset-0 bg-[#0a1920]/60" />}
      <div className={`absolute inset-x-3 mx-auto max-w-md space-y-3 rounded-3xl bg-card p-5 shadow-2xl md:left-auto md:right-8 md:mx-0 ${atTop ? "top-16 md:top-20" : "bottom-24 md:bottom-8"}`} data-testid="demo-tour-card">
        <div className="flex items-center gap-2 text-xs font-bold text-primary"><Sparkles className="h-4 w-4" /> Visite guidée · {state.i + 1}/{state.steps.length}
          <button type="button" onClick={() => setState(null)} className="ml-auto text-muted-foreground hover:text-foreground" aria-label="Quitter la visite" data-testid="demo-tour-close"><X className="h-4 w-4" /></button></div>
        <h3 className="text-lg font-extrabold" data-testid="demo-tour-title">{step.title}</h3>
        <p className="text-sm text-muted-foreground">{step.text}</p>
        <div className="flex items-center gap-2">
          {state.i > 0 && <Button variant="ghost" size="sm" className="rounded-full" onClick={() => move(-1)} data-testid="demo-tour-prev"><ArrowLeft className="mr-1 h-4 w-4" />Précédent</Button>}
          <Button size="sm" className="ml-auto rounded-full" onClick={() => (last ? setState(null) : move(1))} data-testid="demo-tour-next">{last ? "Terminer" : "Suivant"}{!last && <ArrowRight className="ml-1 h-4 w-4" />}</Button>
        </div>
      </div>
    </div>
  );
};
